import React, { useState, useEffect } from "react";
import { useNavigate } from "./utils/localizedNav";
import { useTranslation } from "react-i18next";
import { API_URL } from "./api";
import { fonts, colors } from "./designTokens";
import { useTheme } from "./ThemeContext";
import useIsMobile from "./hooks/useIsMobile";

const HeroWave = ({ isMobile }) => (
  <svg
    viewBox="0 0 1440 160"
    width="100%"
    height={isMobile ? 24 : 55}
    style={{
      position: "absolute",
      bottom: -1,
      left: 0,
      zIndex: 2,
      width: "100%",
      display: "block",
      pointerEvents: "none",
      filter: "drop-shadow(0 2px 18px #ffe28f44)",
    }}
    aria-hidden="true"
  >
    <path
      d="M0,80 Q160,120 350,95 T650,105 T1000,65 Q1290,10 1440,50 V160 H0 Z"
      fill={colors.cream}
      opacity="0.92"
    />
    <ellipse
      cx={isMobile ? 1150 : 1290}
      cy={isMobile ? 35 : 45}
      rx={isMobile ? 27 : 41}
      ry={isMobile ? 9 : 17}
      fill="#fffcc4"
      opacity="0.44"
    />
    <ellipse
      cx={isMobile ? 250 : 480}
      cy={isMobile ? 27 : 34}
      rx={isMobile ? 14 : 23}
      ry={isMobile ? 7 : 12}
      fill="#fffcc4"
      opacity="0.38"
    />
  </svg>
);

export default function HeroSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const [stats, setStats] = useState({ farts: null, users: null });
  const [statsLoading, setStatsLoading] = useState(true);


  // Live-Stats laden (öffentliche Fürze + User)
  useEffect(() => {
    let cancelled = false;

    async function loadStats() {
      try {
        setStatsLoading(true);

        const res = await fetch(`${API_URL}/api/stats`);
        const data = res.ok ? await res.json() : {};

        if (cancelled) return;

        setStats({
          farts: data.fartCount ?? null,
          users: data.userCount ?? null,
        });
      } catch (e) {
        console.error("Stats load failed:", e.message);
        if (!cancelled) {
          setStats({ farts: null, users: null });
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFartButton = () => {
    const section = document.getElementById("fart-upload-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <>
    <div
      style={{
        position: "relative",
        width: "100%",
        height: isMobile ? "calc(100svh - 60px)" : "calc(100vh - 70px)",
        minHeight: isMobile ? 400 : 480,
        maxHeight: isMobile ? 680 : 900,
        marginBottom: 0,
        overflow: "hidden",
        borderRadius: isMobile ? 18 : 34,
        boxShadow: isMobile
          ? "0 2px 16px #f7de7b55"
          : "0 10px 46px #fffbe7ee, 0 2px 12px #ffe28f66",
        background: colors.heroContainer,
        border: isDark ? "2px solid rgba(100,80,255,0.15)" : "2px solid #ffed8b88",
        touchAction: "manipulation",
      }}
    >
      <picture>
        <source
          type="image/webp"
          srcSet="/hero-bg-700.webp 700w, /hero-bg-1200.webp 1200w"
          sizes="(max-width: 768px) 100vw, 1200px"
        />
        <img
          src="/hero-bg-1200.webp"
          srcSet="/hero-bg-700.webp 700w, /hero-bg-1200.webp 1200w"
          sizes="(max-width: 768px) 100vw, 1200px"
          alt={t("hero.img_alt")}
          loading="eager"
          decoding="async"
          fetchpriority="high"
          width={1200}
          height={800}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center center",
            display: "block",
            filter: "brightness(0.98) saturate(1.06) blur(0.25px)",
            opacity: 0.99,
            borderRadius: isMobile ? 18 : 34,
            userSelect: "none",
            pointerEvents: "none",
          }}
          draggable={false}
        />
      </picture>

      {/* Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: isMobile
            ? colors.heroOverlayMobile
            : colors.heroOverlayDesktop,
          zIndex: 1,
          borderRadius: isMobile ? 18 : 34,
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: isMobile ? "36%" : "50%",
          transform: "translateY(-50%)",
          width: "100%",
          textAlign: "center",
          zIndex: 2,
          userSelect: "none",
          padding: isMobile ? "0 14px 18px 14px" : "0 28px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        {/* Headline */}
        <div
          style={{
            color: colors.greenText,
            fontFamily: fonts.heading,
            fontWeight: 900,
            fontSize: isMobile ? 26 : "clamp(38px, 7vw, 60px)",
            letterSpacing: isMobile ? 1.1 : 2.4,
            lineHeight: 1.06,
            textShadow:
              "0 5px 32px #fffbcf, 0 1px 2px #fff, 0 2px 15px #e5db66cc",
            filter: "drop-shadow(0 3px 16px #fff9c577)",
            marginBottom: isMobile ? 10 : 18,
            animation: "fartHeadlineJump 2.1s infinite alternate",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isMobile ? 4 : 10,
            flexWrap: "wrap",
            maxWidth: "100%",
            transformOrigin: "center center",
          }}
        >
          <span
            role="img"
            aria-label="Fart cloud"
            style={{
              fontSize: isMobile ? 28 : 54,
              marginBottom: isMobile ? 0 : 4,
              animation:
                "heroFartBounce 1.6s infinite cubic-bezier(.52,.06,.53,.92)",
              filter: "drop-shadow(0 2px 10px #fff9c555)",
            }}
          >
            💨
          </span>
          Art-of-Fart.com
        </div>

        {/* Subheadline — Pill auf Desktop, Plain Text auf Mobile */}
        <div
          style={{
            color: colors.gold,
            fontFamily: fonts.body,
            fontWeight: 700,
            fontSize: isMobile ? 13 : 20,
            letterSpacing: 0.6,
            textShadow: "0 2px 10px #fffbe7",
            background: isMobile ? "transparent" : (isDark ? "rgba(18,16,32,0.88)" : "rgba(255,255,235,0.96)"),
            display: isMobile ? "block" : "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: isMobile ? 0 : 26,
            padding: isMobile ? "0 4px" : "7px 26px",
            marginTop: isMobile ? 6 : 4,
            boxShadow: isMobile ? "none" : "0 1px 10px #ffe28f88",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <span
            role="img"
            aria-label="Loudspeaker"
            style={{
              fontSize: isMobile ? 16 : 24,
              animation: "speakerShake 2.2s infinite alternate",
              marginRight: 6,
            }}
          >
            🔊
          </span>
          {t("hero.subheadline")}
        </div>

        {/* Trust-Line — Badge-Chips */}
        <div
          style={{
            marginTop: isMobile ? 12 : 18,
            fontFamily: fonts.body,
            fontSize: isMobile ? 12.5 : 14,
            color: colors.goldText,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: isMobile ? 6 : 10,
            maxWidth: "100%",
          }}
        >
          {[
            { icon: "🔒", text: t("hero.trust.anonymous") },
            { icon: "🎧", text: t("hero.trust.free") },
            { icon: "🏆", text: t("hero.trust.xp") },
          ].map(({ icon, text }) => (
            <span
              key={text}
              style={{
                background: colors.heroTrustBg,
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(200,168,0,0.22)",
                borderRadius: 999,
                padding: isMobile ? "4px 11px" : "3px 10px",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                whiteSpace: "nowrap",
                fontWeight: 600,
                boxShadow: "0 1px 4px rgba(200,160,0,0.10)",
              }}
            >
              {icon} {text}
            </span>
          ))}
        </div>

        {/* CTA – nur ein Button */}
        <div
          style={{
            marginTop: isMobile ? 16 : 24,
            display: "flex",
            flexDirection: "column",
            gap: 9,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <button
            className="hero-cta-btn"
            onClick={handleFartButton}
            style={{
              background: colors.ctaGradient,
              border: "none",
              padding: isMobile ? "9px 22px" : "14px 36px",
              borderRadius: 40,
              cursor: "pointer",
              fontSize: isMobile ? 16 : 20,
              fontWeight: 900,
              color: colors.greenDark,
              boxShadow: "0 4px 20px #a8ffcf77, 0 2px 6px #0a8a44aa",
              fontFamily: fonts.heading,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            type="button"
          >
            🎤 {t("hero.cta")}
          </button>
        </div>

        {/* Live-Stats — größer + Puls-Dot */}
        {!statsLoading &&
          stats.farts !== null &&
          stats.users !== null && (
            <div
              style={{
                marginTop: isMobile ? 12 : 14,
                fontFamily: fonts.body,
                fontSize: isMobile ? 13.5 : 14,
                color: isDark ? colors.textMuted : colors.goldText,
                background: colors.heroStatsBg,
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(200,168,0,0.25)",
                borderRadius: 999,
                padding: isMobile ? "5px 14px" : "6px 18px",
                boxShadow: "0 2px 10px #fff2c5bb",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? 7 : 8,
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#e53e3e", display: "inline-block",
                  flexShrink: 0,
                  animation: "liveBlink 1.4s ease infinite",
                }}
              />
              <span>
                {t("hero.stats.prefix")}{" "}
                <strong style={{ color: colors.greenText, fontSize: isMobile ? 15 : 16 }}>{stats.farts}</strong>{" "}
                {t("hero.stats.farts_label")} •{" "}
                <strong style={{ color: colors.greenText, fontSize: isMobile ? 15 : 16 }}>{stats.users}</strong>{" "}
                {t("hero.stats.users_label")}
              </span>
            </div>
          )}
      </div>

      {/* Scroll-Indicator */}
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            bottom: 60,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            opacity: 0.7,
            animation: "scrollBounce 2s infinite",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              fontFamily: fonts.body,
              fontSize: 11,
              color: colors.goldText,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {t("hero.scroll")}
          </span>
          <svg width="22" height="14" viewBox="0 0 22 14" fill="none">
            <path
              d="M1 1L11 11L21 1"
              stroke={colors.goldText}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <HeroWave isMobile={isMobile} />

      <style>
        {`
          @keyframes fartHeadlineJump {
            0% { transform: scale(1) translateY(0); }
            70% { transform: scale(1.02) translateY(-2px); }
            90% { transform: scale(1.06) translateY(-7px); }
            100% { transform: scale(1.03) translateY(-4px); }
          }
          @keyframes heroFartBounce {
            0% { transform: translateY(0) scale(1); }
            30% { transform: translateY(-6px) scale(1.08); }
            100% { transform: translateY(0) scale(1); }
          }
          @keyframes speakerShake {
            0% { transform: rotate(-8deg); }
            40% { transform: rotate(8deg); }
            100% { transform: rotate(0deg); }
          }
          @keyframes liveBlink {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.25; }
          }
          .hero-cta-btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 24px #a8ffcfaa, 0 3px 10px #0a8a44aa !important;
          }
          @keyframes scrollBounce {
            0%, 100% { transform: translateX(-50%) translateY(0); }
            50% { transform: translateX(-50%) translateY(8px); }
          }
        `}
      </style>
    </div>
    </>
  );
}